import { useEffect, useState } from 'react'
import { api, type Desk, type LicenceStatus, type MarketProfile } from '../api'
import { RowMenu } from '../components/RowMenu'

/**
 * Desks — IMPLEMENTATION.md decisions 73–78.
 *
 * A desk is one of the firm's investment teams, and every fund is on exactly one.
 * Setting a desk up with a market adds that market's starter vocabulary to the
 * firm's one dictionary and records the choice on the desk (decision 75).
 *
 * **User-facing text here says what to do, not why the design is what it is.**
 */
export function Desks({
  canManage = true,
  onView,
}: {
  /** Adding, renaming and setting up desks is a whole-firm administrator's. */
  canManage?: boolean
  /** Open the funds or the groups of one desk. */
  onView?: (screen: 'funds' | 'groups', deskId: string) => void
} = {}) {
  const [desks, setDesks] = useState<Desk[]>([])
  const [markets, setMarkets] = useState<MarketProfile[]>([])
  const [lic, setLic] = useState<LicenceStatus | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [fresh, setFresh] = useState({ code: '', name: '' })
  /** The desk being renamed, and its draft. */
  const [renaming, setRenaming] = useState<Desk | null>(null)
  const [draft, setDraft] = useState({ code: '', name: '' })
  /** The desk a market is being added to, and the market chosen. */
  const [marketFor, setMarketFor] = useState<Desk | null>(null)
  const [market, setMarket] = useState('')

  async function load() {
    try {
      setDesks(await api.desks())
      setLic(await api.licence().catch(() => null))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoaded(true)
    }
  }

  useEffect(() => {
    void load()
    if (canManage) api.marketProfiles().then(setMarkets).catch(() => setMarkets([]))
  }, [])

  async function act(work: () => Promise<void>) {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await work()
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const title = (name: string) => markets.find((m) => m.name === name)?.title ?? name
  /** Every desk the licence covers is set up. */
  const full = lic !== null && lic.desks !== null && desks.length >= lic.desks

  if (!loaded) return <p className="loading">Loading…</p>

  return (
    <section>
      <header className="page-head">
        <h2>Desks</h2>
        <p className="note">
          The firm’s investment teams. Every fund is run by one desk, and a desk can group its funds under shared
          limits. Open a desk’s ⋯ menu to see its funds and groups.
        </p>
      </header>

      {error && <p className="error">{error}</p>}
      {notice && <p className="ok">{notice}</p>}

      {canManage && (
      <div className="card">
        <h3>Add a desk</h3>
        {lic && lic.desks !== null && (
          <p className="note" data-testid="desk-allowance">
            {desks.length} of {lic.desks} licensed desks in use.
            {full && ' Adding another is a licence update from DORON Tech Solutions.'}
          </p>
        )}
        <form
          className="row-form"
          onSubmit={(e) => {
            e.preventDefault()
            void act(async () => {
              const made = await api.createDesk(fresh)
              setNotice(`${made.code} added. Put funds on it from View funds.`)
              setFresh({ code: '', name: '' })
            })
          }}
        >
          <label>
            Code
            <input
              value={fresh.code}
              required
              title="A short code the firm already uses for the team — EMD, MULTI, MMF."
              onChange={(e) => setFresh({ ...fresh, code: e.target.value })}
            />
          </label>
          <label>
            Name
            <input
              value={fresh.name}
              required
              title="The team's name as people refer to it."
              onChange={(e) => setFresh({ ...fresh, name: e.target.value })}
            />
          </label>
          <button type="submit" disabled={busy || full}>
            Add
          </button>
        </form>
      </div>
      )}

      {renaming && (
        <div className="card" data-testid="desk-edit">
          <h3>Rename {renaming.code}</h3>
          <form
            className="row-form"
            onSubmit={(e) => {
              e.preventDefault()
              void act(async () => {
                const saved = await api.updateDesk(renaming.id, draft)
                setRenaming(null)
                setNotice(`${saved.code} saved.`)
              })
            }}
          >
            <label>
              Code
              <input value={draft.code} required onChange={(e) => setDraft({ ...draft, code: e.target.value })} />
            </label>
            <label>
              Name
              <input value={draft.name} required onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </label>
            <button type="submit" disabled={busy}>
              Save
            </button>
            <button type="button" className="ghost" onClick={() => setRenaming(null)}>
              Cancel
            </button>
          </form>
        </div>
      )}

      {marketFor && (
        <div className="card" data-testid="desk-market">
          <h3>Add a market to {marketFor.code}</h3>
          <form
            className="row-form"
            onSubmit={(e) => {
              e.preventDefault()
              if (!market) return
              void act(async () => {
                const result = await api.addDeskMarket(marketFor.id, market)
                setMarketFor(null)
                setMarket('')
                setNotice(
                  result.total_added
                    ? `${title(market)} added to ${marketFor.code}: ${result.total_added} new terms in the dictionary.`
                    : `${title(market)} added to ${marketFor.code}. Its terms were already in the dictionary.`,
                )
              })
            }}
          >
            <label>
              Market
              <select value={market} required onChange={(e) => setMarket(e.target.value)}>
                <option value="">Choose a market…</option>
                {markets
                  .filter((m) => !marketFor.markets.includes(m.name))
                  .map((m) => (
                    <option key={m.name} value={m.name} title={m.description}>
                      {m.title}
                    </option>
                  ))}
              </select>
            </label>
            <button type="submit" disabled={busy || !market}>
              Add
            </button>
            <button type="button" className="ghost" onClick={() => setMarketFor(null)}>
              Cancel
            </button>
          </form>
          {market && <p className="note">{markets.find((m) => m.name === market)?.description}</p>}
        </div>
      )}

      <h3>{desks.length} desk(s)</h3>
      <table className="grid">
        <thead>
          <tr>
            <th>Code</th>
            <th>Name</th>
            <th>Funds</th>
            <th>Markets</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {desks.map((desk) => (
            <tr key={desk.id} data-testid="desk-row">
              <td>
                <code>{desk.code}</code>
              </td>
              <td>{desk.name}</td>
              <td className="limit">{desk.fund_count}</td>
              <td className="clause">{desk.markets.map(title).join(', ')}</td>
              <td className="run-actions">
                <RowMenu
                  label={`Actions for ${desk.code}`}
                  items={[
                    ...(onView
                      ? [
                          { label: 'View funds', onSelect: () => onView('funds', desk.id) },
                          { label: 'View groups', onSelect: () => onView('groups', desk.id) },
                        ]
                      : []),
                    ...(canManage ? [
                    {
                      label: 'Rename',
                      onSelect: () => {
                        setMarketFor(null)
                        setRenaming(desk)
                        setDraft({ code: desk.code, name: desk.name })
                      },
                    },
                    {
                      label: 'Add a market',
                      disabled: desk.markets.length >= markets.length,
                      why: desk.markets.length >= markets.length ? 'Every market is already added.' : undefined,
                      onSelect: () => {
                        setRenaming(null)
                        setMarketFor(desk)
                        setMarket('')
                      },
                    },
                    {
                      label: 'Delete',
                      danger: true,
                      disabled: desk.fund_count > 0 || desks.length === 1,
                      why:
                        desk.fund_count > 0
                          ? 'Move its funds to another desk first.'
                          : desks.length === 1
                            ? 'Every fund needs a desk, so the firm keeps at least one.'
                            : undefined,
                      onSelect: () => {
                        if (!window.confirm(`Delete ${desk.code}? It runs no funds.`)) return
                        void act(async () => {
                          await api.deleteDesk(desk.id)
                          setNotice(`${desk.code} deleted.`)
                        })
                      },
                    },
                    ] : []),
                  ]}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}
